import Divider from '@/components/divider/Divider'
import Image from '@/components/image/Image'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import YouTubeIcon from '@mui/icons-material/YouTube'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import React from 'react'
import notelist from './notelist.json'
import schema from './schema.json'

interface Song {
  name: string
  link?: string
  type?: 'youtube' | 'external'
  comment?: string
}

interface NoteCategory {
  type: string
  comment?: string // Optional property
  songs?: (string | Song)[]
}

const notelistData = notelist as { types: NoteCategory[] }

const Members: React.FC = () => {
  return (
    <Grid
      container
      direction="column"
      columnSpacing={2}
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      marginBottom="50px"
    >
      <Grid size={10}>
        <h1>För våra musikanter</h1>
        <p>Här finner du allt som är relevant för dig som är medlem i kåren.</p>
      </Grid>
      <Grid size={10}>
        <h2>Musikkårens repetitions- och spelprogram för 2025</h2>
      </Grid>
      <Grid
        container
        direction="row"
        size={{ xs: 11, md: 8 }}
        justifyContent="center"
        alignItems="flex-start"
        textAlign="start"
      >
        <Grid size={{ xs: 11, md: 12 }} display="flex" justifyContent="center">
          <p style={{ textAlign: 'left', margin: '0 0 20px 0' }}>
            Förklaring till programmets utformning:
            <br />
            Vanlig text = vanlig repetition
            <br />
            <span style={{ textDecoration: 'underline' }}>
              Understruken text = viktig repetition
            </span>
            <br />
            <strong>Fet text = konsert</strong>
            <br />
            (OBS! Med reservation om kommande ändringar)
          </p>
        </Grid>

        <Grid container spacing={4} size={{ xs: 12, md: 12 }}>
          {schema.months.map((month, index) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              <h3
                style={{
                  textAlign: 'left',
                  borderBottom: '2px solid #eaeaea',
                  paddingBottom: '5px',
                }}
              >
                {month.month}
              </h3>
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  tableLayout: 'fixed', // Låser kolumnbredderna så allt alignar perfekt
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: '50px', // Fast bredd för Dag-kolumnen
                        fontWeight: 600,
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        fontSize: '0.85rem',
                        paddingBottom: '8px',
                        color: '#666',
                      }}
                    >
                      Dag
                    </th>
                    <th
                      style={{
                        fontWeight: 600,
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        fontSize: '0.85rem',
                        paddingBottom: '8px',
                        color: '#666',
                      }}
                    >
                      Aktivitet
                    </th>
                    <th
                      style={{
                        width: '110px', // Fast bredd för Tid-kolumnen
                        fontWeight: 600,
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        fontSize: '0.85rem',
                        paddingBottom: '8px',
                        color: '#666',
                      }}
                    >
                      Tid
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {month.days.map((day, dayIndex) => {
                    const isImportant = day.type === 'important'
                    const isConcert = day.type === 'required'

                    const textDecoration = isImportant ? 'underline' : 'none'
                    const fontWeight = isConcert ? 700 : 400

                    return (
                      <tr key={dayIndex}>
                        <td
                          style={{
                            padding: '6px 0',
                            textAlign: 'left',
                            textDecoration,
                            fontWeight,
                          }}
                        >
                          {day.day}
                        </td>
                        <td
                          style={{
                            padding: '6px 0',
                            textAlign: 'left',
                            textDecoration,
                            fontWeight,
                            paddingRight: '15px',
                          }}
                        >
                          {day.text}
                        </td>
                        <td
                          style={{
                            padding: '6px 0',
                            textAlign: 'left',
                            fontWeight,
                          }}
                        >
                          {day.time ?? ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Divider size={{ xs: 11, md: 10 }} />
      <Grid size={10}>
        <h2>Noteflight</h2>
        <p>
          Scoutmusikkåren använder sig av Noteflight där kommande konsertlåtar
          ligger.
        </p>
        <Button
          variant="contained"
          href="https://www.noteflight.com/groups/group/73126/scores/all"
          target="_blank"
          rel="noreferrer"
          sx={{
            color: '#FFFFFF',
            width: 150,
            height: 40,
          }}
        >
          <>
            Noteflight{' '}
            <Image
              src="/images/statics/noteflight-icon.png"
              alt="Noteflight Icon"
            />
          </>
        </Button>
      </Grid>
      <Grid size={10}>
        <h2>Notarkiv</h2>
        <p>
          Det finns även ett notarkiv. Ladda gärna ner listan och se om det är
          något du saknar i repertoaren. Den uppdateras kontinuerligt.
        </p>
        <Button
          variant="contained"
          href="https://docs.google.com/spreadsheets/d/1wEGhr39eQolWYdGPk56UghuFfLG94Jy8"
          target="_blank"
          rel="noreferrer"
          sx={{
            color: '#FFFFFF',
            width: 150,
            height: 40,
          }}
        >
          Notarkiv <FileDownloadIcon />
        </Button>
      </Grid>
      <Grid size={10}>
        <h2>Låtlista</h2>
        <p>
          Dessa låtar ska du ha i 4 delar i din mapp (Konsertlåtar, Vårlåtar,
          Jullåtar, Gatukonsert)
        </p>
      </Grid>
      <Grid container offset={2}>
        {notelistData.types.map((month, index) => (
          <Grid key={index} size={{ xs: 11, md: 6 }} textAlign="start">
            <h3>{month.type}</h3>
            {month.comment ? (
              month.comment
            ) : (
              <ul>
                {month.songs?.sort().map((song, index) => (
                  <li key={index}>
                    {typeof song === 'string' ? (
                      song
                    ) : (
                      <>
                        {song.link ? (
                          <>
                            <Link
                              href={song.link}
                              target="_blank"
                              display="flex"
                            >
                              {song.name}{' '}
                              {song.type === 'youtube' ? (
                                <YouTubeIcon />
                              ) : (
                                <OpenInNewIcon />
                              )}
                            </Link>
                            <Typography variant="body2">
                              {song.comment && <i>{song.comment}</i>}
                            </Typography>
                          </>
                        ) : (
                          <>{song.name}</>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}

export default Members
